/**
 * Pure helpers for the proposed-fix change list and its PDF review dialog.
 *
 * @module components/regulation/finding/changes/proposedFix
 */
import type { DocumentChangesView, FindingPdfEdit } from "@repo/shared/findingChanges";

/** "4.2 Risk factors" → § 4.2 and "Risk factors"; a section without a number keeps its name. */
export function splitSection(section: string): { readonly number: string | null; readonly name: string } {
  const match = /^(?:§\s*)?(\d+[A-Z]?(?:\.\d+)*)\.?\s+(.*)$/.exec(section.trim());
  const name = match ? match[2]! : section.trim();
  return { number: match ? match[1]! : null, name: name.charAt(0).toUpperCase() + name.slice(1) };
}

/** "p. 3", or "p. 3 → 4" when the passage moves page in the proposed PDF. */
export function pageLabel(edit: Pick<FindingPdfEdit, "beforePage" | "afterPage">): string {
  return edit.afterPage === edit.beforePage ? `p. ${edit.beforePage}` : `p. ${edit.beforePage} → ${edit.afterPage}`;
}

/** What one edit does to the document, in two words. */
export function editKind(edit: Pick<FindingPdfEdit, "beforeText" | "afterText">): "added" | "removed" | "changed" {
  if (!edit.beforeText.trim()) return "added";
  if (!edit.afterText.trim()) return "removed";
  return "changed";
}

/**
 * The proposal narrowed to one edit, per edit, for a review that shows one
 * change at a time. The file URLs stay the proposal's, so the PDF renderer
 * (keyed by them) is reused when moving between changes.
 */
export function singleEditViews(proposal: DocumentChangesView): ReadonlyArray<DocumentChangesView> {
  return proposal.edits.map((edit) => ({ ...proposal, edits: [edit] }));
}

/** The next index within [0, total), clamped. */
export function clampChange(index: number, total: number): number {
  return Math.min(Math.max(total - 1, 0), Math.max(0, index));
}

/**
 * The change dialog's keys: j, ↓ or → for the next change, k, ↑ or ← for the
 * previous one; 0 for anything else, including chords and typing in a field.
 */
export function changeStepForKey(event: {
  readonly key: string;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly altKey: boolean;
  readonly typing: boolean;
}): -1 | 0 | 1 {
  if (event.typing || event.metaKey || event.ctrlKey || event.altKey) return 0;
  if (event.key === "j" || event.key === "J" || event.key === "ArrowDown" || event.key === "ArrowRight") return 1;
  if (event.key === "k" || event.key === "K" || event.key === "ArrowUp" || event.key === "ArrowLeft") return -1;
  return 0;
}
